window.StockCubeCore = window.StockCubeCore || {};

window.StockCubeCore.createProperZeroFrame = function (stocks) {
    if (!stocks || stocks.length === 0) {
        if (__LOG_WARN__) AppLog.warn('No stocks provided to createProperZeroFrame');
        return [];
    }

    const zeroFrameStocks = stocks.map(stock => ({
        ...stock,
        todaysChangePerc: 0,
        cumulative_volume: 0,
    }));

    return zeroFrameStocks;
};

window.StockCubeCore.calculateFrameDelay = function (timeCount) {
    const maxFrames = 20;
    return timeCount > maxFrames ? 250 : 500;
};

window.StockCubeCore.getConfigSetting = function (key, defaultValue) {
    const cfg = this.configSettings;
    if (!cfg) return defaultValue;
    const flat = (cfg.value && typeof cfg.value === 'object') ? cfg.value : cfg;
    const val = flat[key];
    return val !== undefined ? val : defaultValue;
};

window.StockCubeCore.prepareAnimationTimes = function (intervals) {
    const times = Object.keys(intervals).sort();
    const maxFrames = 20;
    let displayTimes = times;
    if (times.length > maxFrames) {
        const step = Math.ceil(times.length / maxFrames);
        displayTimes = times.filter((_, i) => i % step === 0);
    }
    return displayTimes;
};

window.StockCubeCore.waitForEntityReady = function (elOrSelector, timeoutMs = 3000) {
    try {
        return new Promise((resolve) => {
            const el = typeof elOrSelector === 'string' ? document.querySelector(elOrSelector) : elOrSelector;
            if (!el) { resolve(null); return; }
            if (el.object3D) { resolve(el); return; }
            let done = false;
            const onLoad = () => { if (!done) { done = true; el.removeEventListener('loaded', onLoad); resolve(el); } };
            el.addEventListener('loaded', onLoad, { once: true });
            setTimeout(() => { if (!done) { done = true; el.removeEventListener('loaded', onLoad); resolve(el); } }, timeoutMs);
        });
    } catch (_) {
        return Promise.resolve(null);
    }
};

window.StockCubeCore.setupTimelineForAnimation = async function (displayTimes) {
    try {
        if (!this.data.timelineEntity || displayTimes.length === 0) return;

        await this.waitForEntityReady(this.data.timelineEntity);

        this.data.timelineEntity.setAttribute('timeline-control', { timestamps: displayTimes });

        const stockChartEntity = document.querySelector('[stock-candlestick-chart]');
        if (stockChartEntity) {
            stockChartEntity.emit('timeline-data-available', { timestamps: displayTimes });
        }

        const tl = this.data.timelineEntity.components && this.data.timelineEntity.components['timeline-control'];
        if (tl) {
            if (tl.isAnimating && typeof tl.stopAnimation === 'function') {
                tl.stopAnimation();
            }
            tl.isAnimating = true;
            this._autoAnimationSession = tl.currentAnimationSession || 0;
            if (tl.isAnimationOn !== false) {
                tl.updatePlayButtonIcon && tl.updatePlayButtonIcon(true);
            }
        } else {
            this.data.timelineEntity.addEventListener('loaded', () => {
                try {
                    const t2 = this.data.timelineEntity.components && this.data.timelineEntity.components['timeline-control'];
                    if (t2) {
                        if (t2.isAnimating && typeof t2.stopAnimation === 'function') {
                            t2.stopAnimation();
                        }
                        t2.isAnimating = true;
                        this._autoAnimationSession = t2.currentAnimationSession || 0;
                        if (t2.isAnimationOn !== false) {
                            t2.updatePlayButtonIcon && t2.updatePlayButtonIcon(true);
                        }
                    }
                } catch (_) { }
            }, { once: true });
        }
    } catch (e) {
        try { if (__LOG_WARN__) AppLog.warn('[Stock Visualizer] setupTimelineForAnimation failed:', e && e.message ? e.message : e); } catch (_) { }
    }
};

window.StockCubeCore.animateStockBars = async function (result, fetchId) {
    this.fireworksController?.clearFireworkIntervals();
    const { intervals } = result;
    this.currentData = result;

    const displayTimes = this.prepareAnimationTimes(intervals);

    if (displayTimes.length === 0) {
        if (this.isHasSectors) {
            this.updateBarsSectors([], this.data.topN, this.colorMode, false);
        } else {
            this.updateBarsNoSectors([], this.data.topN, false);
        }
        if (this.data.timelineEntity && this.data.timelineEntity.components['timeline-control']) {
            this.data.timelineEntity.setAttribute('timeline-control', { timestamps: [] });
            this.data.timelineEntity.components['timeline-control'].isAnimating = false;
        }
        this.isFirstAnimationPlay = false;
        this.finalizeAnimation(displayTimes);
        return;
    }

    const shouldWaitForChart = this.waitingForChartData;

    if (shouldWaitForChart && !this.isChartDataReady) {
        this.pendingAnimationData = { result, fetchId };
        return;
    }

    await this.setupTimelineForAnimation(displayTimes);

    const isAnimationOn = this.getConfigSetting('isAnimationOn', true);
    if (!isAnimationOn && this.data.dataSource !== 'dividend' && this.data.dataSource !== 'sip') {
        const finalTime = displayTimes[displayTimes.length - 1];
        const stocks = intervals[finalTime];
        this.currentTimestamp = finalTime;

        if (this.data.timelineEntity) {
            this.data.timelineEntity.emit('timeline-update', {
                timestamp: finalTime,
                index: displayTimes.length - 1,
                total: displayTimes.length
            }, true);
        }

        await this.renderAnimationFrame(stocks);
        this.isFirstAnimationPlay = false;
        this.finalizeAnimation(displayTimes);
        return;
    }

    const frameDelay = this.calculateFrameDelay(displayTimes.length);

    if (this.isFirstAnimationPlay && !this.hasShownZeroFrame && displayTimes.length > 0) {
        const firstTimestamp = displayTimes[0];
        const firstStocks = intervals[firstTimestamp];

        if (this.data.dataSource === 'watchlist-reports') {
            firstStocks.sort((a, b) => (a.ranking || 0) - (b.ranking || 0));
        }

        const zeroFrameStocks = this.createProperZeroFrame(firstStocks);

        this.currentTimestamp = 'ZERO_FRAME';
        await this.renderAnimationFrame(zeroFrameStocks);
        await new Promise(resolve => setTimeout(resolve, frameDelay));

        this.hasShownZeroFrame = true;
        this.maxHeightValue = undefined;
    }

    for (let i = 0; i < displayTimes.length; i++) {
        const currentIsAnimationOn = this.getConfigSetting('isAnimationOn', true);
        if (!currentIsAnimationOn && this.data.dataSource !== 'dividend' && this.data.dataSource !== 'sip') {
            const finalTime = displayTimes[displayTimes.length - 1];
            const stocks = intervals[finalTime];
            this.currentTimestamp = finalTime;

            if (this.data.timelineEntity) {
                this.data.timelineEntity.emit('timeline-update', {
                    timestamp: finalTime,
                    index: displayTimes.length - 1,
                    total: displayTimes.length
                }, true);
            }

            await this.renderAnimationFrame(stocks);
            break;
        }

        if (fetchId !== this.currentFetchId) {
            this.fireworksController?.clearFireworkIntervals();
            this.refreshEmissiveForAllBars();
            return;
        }

        const tl = this.data.timelineEntity && this.data.timelineEntity.components['timeline-control'];
        if (tl && tl.isDragging) {
            tl.isAnimating = false;
            tl.updatePlayButtonIcon && tl.updatePlayButtonIcon(false);
            this.fireworksController?.clearFireworkIntervals();
            this.refreshEmissiveForAllBars();
            return;
        }
        if (tl && tl.currentAnimationSession !== undefined && tl.currentAnimationSession !== this._autoAnimationSession) {
            this.fireworksController?.clearFireworkIntervals();
            this.refreshEmissiveForAllBars();
            return;
        }

        const time = displayTimes[i];
        const stocks = intervals[time];
        this.currentTimestamp = time;
        this.updateTimelineProgress(time, i, displayTimes.length);
        await this.renderAnimationFrame(stocks);
        await new Promise(resolve => setTimeout(resolve, frameDelay));
    }

    this.isFirstAnimationPlay = false;
    this.finalizeAnimation(displayTimes);
};

window.StockCubeCore.cleanupAnimationEarlyStop = function () {
    if (__LOG_INFO__) AppLog.info(`Fetch outdated in animateStockBars, stopping`);
    if (this.data.timelineEntity) {
        const _tlStop = this.data.timelineEntity.components['timeline-control'];
        if (_tlStop) {
            _tlStop.isAnimating = false;
        }
        if (__LOG_INFO__) AppLog.info('[Stock Visualizer] Reset timeline isAnimating to false (early stop)');
    }
    this.fireworksController?.clearFireworkIntervals();
    this.refreshEmissiveForAllBars();
};

window.StockCubeCore.finalizeAnimation = function (displayTimes) {
    this.fireworksController?.setupPostAnimation(displayTimes);

    if (window.PreloaderManager) window.PreloaderManager.hide();

    if (this.data.timelineEntity) {
        const _tlFinal = this.data.timelineEntity.components['timeline-control'];
        if (_tlFinal) {
            _tlFinal.isAnimating = false;
            _tlFinal.updatePlayButtonIcon && _tlFinal.updatePlayButtonIcon(false);
        }
        if (__LOG_INFO__) AppLog.info('[Stock Visualizer] Reset timeline isAnimating to false (animation complete)');

        const finalTime = displayTimes[displayTimes.length - 1];
        this.data.timelineEntity.emit('timeline-update', {
            timestamp: finalTime,
            index: displayTimes.length - 1,
            total: displayTimes.length
        }, true);
    }

    this.refreshEmissiveForAllBars();
    this.afterRenderFinalFrame();
};

window.StockCubeCore.startAnimationOrRenderFinal = async function (result, fetchId) {
    const isAnimationOn = this.getConfigSetting('isAnimationOn', true);
    if (!isAnimationOn && this.data.dataSource !== 'sip' && this.data.dataSource !== 'dividend') {
        this.renderFinalFrame(result);
        return;
    }
    try {
        await this.animateStockBars(result, fetchId);
    } catch (e) {
        try { if (__LOG_WARN__) AppLog.warn('[Stock Visualizer] animateStockBars failed; animation aborted safely', e && e.message ? e.message : e); } catch (_) { }
    }
};

window.StockCubeCore.renderFinalFrame = function (result) {
    if (!result || !result.intervals) return;
    const displayTimes = Object.keys(result.intervals).sort();
    if (displayTimes.length === 0) {
        if (this.isHasSectors) {
            this.updateBarsSectors([], this.data.topN, this.colorMode, false);
        } else {
            this.updateBarsNoSectors([], this.data.topN, false);
        }
        return;
    }

    if (this.data.timelineEntity) {
        this.data.timelineEntity.setAttribute('timeline-control', { timestamps: displayTimes });
    }

    const finalTime = displayTimes[displayTimes.length - 1];
    const finalStocks = result.intervals[finalTime];
    this.currentTimestamp = finalTime;

    this.renderFinalFrameStockContent(finalStocks);

    this.finalizeAnimation(displayTimes);
    this.isFirstAnimationPlay = false;
};

window.StockCubeCore.renderAnimationFrame = async function (stocks) {
    if (this.isFirstRender) {
        if (this.isHasSectors) {
            await this.renderStockBars(stocks, this.data.topN);
        } else {
            await this.renderStockBars(stocks, undefined);
        }
        this.isFirstRender = false;
        this.isInitialized = true;
    } else if (this.isHasSectors) {
        this.updateBarsSectors(stocks, this.data.topN, this.colorMode, this.isFixed);
    } else {
        this.updateBarsNoSectors(stocks, this.data.topN, this.isFixed);
    }
    this.calculateSectorLabelPosition?.(this.rotationData);
};

window.StockCubeCore.afterRenderFinalFrame = function () {

    this.calculateSectorLabelPosition?.(this.rotationData);

    const wrapper = this.el && this.el.parentElement;
    const currentHeightScale = this.heightScale || 1;
    const currentMaxHeightValue = this.maxHeightValue || 1;
    const currentTargetHeight = this.usableHeight ? (this.usableHeight * 0.9) : 1;
    const snapshotDataSource = this.data && this.data.dataSource;

    setTimeout(() => {
        if (!this.data || this.data.dataSource !== snapshotDataSource) return;
        this.adjustChartToFitViewport?.(
            this.stockBars,
            currentHeightScale,
            currentMaxHeightValue,
            currentTargetHeight,
            this.zeroPlane,
            wrapper
        );
        this.updateBillboards?.(true);
        window.StockCubeCore.recalcLabelPositions.call(this);
        this.calculateSectorLabelPosition?.();
    }, 100);
};
